import { redirect } from 'react-router-dom';
import type { MeResponse, Permission, WorkspaceGroup, WorkspaceOrganizationListItem } from '@immo/api-contract';
import { slugIdOf } from '@immo/api-contract';
import { buildSignInRedirect } from './return-to';
import { fetchMe } from './session';
import { membershipForWorkspace, primaryMembership } from './me-membership';
import { isWorkspaceEntryBlocked } from './onboarding';
import { fetchMemberPermissions } from './fetch-member-permissions';
import { canReadAllWorkspaceOrganizations, listWorkspaceOrganizations } from './list-workspace-organizations';
import { readNarrowedOrganizationId, resolveSelectedOrganization } from '../selected-organization';
import { workspacePath } from '../workspace-path';

/**
 * The workspace + main-organisation pair every authenticated page needs before
 * it can fetch its own data. Resolving it requires an `/auth/me` call (for the
 * workspace id), a workspace-organisations fetch (to pick the main org), and a
 * permissions fetch for the selected Standort.
 */
export type WorkspaceContext = {
  readonly me: MeResponse;
  // The workspace UUID — use for API calls (`/workspaces/:id/...`).
  readonly workspaceId: string;
  // The workspace URL slug — use for building in-app links (`/w/:slug/...`).
  readonly workspaceSlug: string;
  readonly organization: WorkspaceOrganizationListItem;
  readonly organizations: ReadonlyArray<WorkspaceOrganizationListItem>;
  readonly permissions: ReadonlyArray<Permission>;
  // The Standort the member has narrowed their visible lists to, or `null` for
  // the default "all visible" view. Validated against `organizations` — a stale
  // id resolves to `null` (unnarrowed).
  readonly narrowedOrganizationId: string | null;
  // True for Owner / Workspace-Admin (can read every Standort); false for a
  // member who only sees their affiliated Standorte. Drives the aggregate label.
  readonly seesAllStandorte: boolean;
};

/**
 * The slug of the workspace to drop a member into when none is named in the URL
 * (fresh entry on bare `/`). The FIRST workspace they can actually enter —
 * skipping any whose company onboarding they cannot complete, since those
 * dead-end on the blocking screen — falling back to the primary membership when
 * every workspace is a dead-end (so the gate still routes them somewhere
 * coherent). `null` only when the user has no memberships at all.
 */
export function resolveDefaultWorkspaceSlug(me: MeResponse): string | null {
  const enterable = me.organisations.find((m) => !isWorkspaceEntryBlocked(me, m.workspaceId));
  const workspaceId = (enterable ?? primaryMembership(me))?.workspaceId;

  if (workspaceId === undefined) return null;

  return me.workspaces.find((w) => w.id === workspaceId)?.slug ?? null;
}

const LAST_WORKSPACE_KEY = 'immo.last-workspace-slug';

/** Remembers the workspace the member last entered, for entry points that carry none (`/connect-extension`). */
export function rememberWorkspaceSlug(slug: string): void {
  try {
    localStorage.setItem(LAST_WORKSPACE_KEY, slug);
  } catch {
    // Storage blocked: those entry points fall back to the default workspace.
  }
}

function readRememberedWorkspaceSlug(): string | null {
  try {
    return localStorage.getItem(LAST_WORKSPACE_KEY);
  } catch {
    return null;
  }
}

/**
 * The workspace the member last entered, while they can still enter it; otherwise the default one
 * ({@link resolveDefaultWorkspaceSlug}).
 */
export function resolveLastWorkspaceSlug(me: MeResponse): string | null {
  const remembered = readRememberedWorkspaceSlug();
  const workspace = remembered === null ? null : resolveWorkspaceGroup(me, remembered);

  if (workspace !== null && !isWorkspaceEntryBlocked(me, workspace.id)) return workspace.slug;

  return resolveDefaultWorkspaceSlug(me);
}

/**
 * Resolve a `/w/:workspaceId` URL segment to the caller's workspace group. The
 * segment is normally the slug (`<name>-<slugId>`), matched on the immutable
 * `slugId` so a stale human prefix still resolves; a raw UUID is also honoured as
 * a permanent fallback (e.g. older or machine-built links).
 */
function resolveWorkspaceGroup(me: MeResponse, param: string): WorkspaceGroup | null {
  const byId = me.workspaces.find((w) => w.id === param);

  if (byId !== undefined) return byId;

  const targetSlugId = slugIdOf(param);

  return me.workspaces.find((w) => slugIdOf(w.slug) === targetSlugId) ?? null;
}

/**
 * Memoised so the `/auth/me` + workspace-organisations round-trips run once per
 * session and are shared across every route loader. React Router runs sibling
 * and ancestor loaders concurrently, so a plain function would fire these
 * requests N times on a cold navigation — the shared promise collapses them to
 * one. Keyed by the resolved workspace so switching workspaces re-resolves rather
 * than replaying the previous one. Cleared on sign-out / after org mutations via
 * `invalidateWorkspaceContext`.
 */
let cache: { readonly key: string; readonly promise: Promise<WorkspaceContext> } | null = null;

/**
 * The `/sign-in` target that preserves the current deep link as `returnTo`
 * (IMM-486). This loader runs in the browser, so the current location is
 * `window.location`; guard against a non-browser context by omitting the
 * returnTo (the callback then falls back to `/`).
 */
function signInRedirectForCurrentLocation(): string {
  if (typeof window === 'undefined') return '/sign-in';

  return buildSignInRedirect(window.location.pathname + window.location.search);
}

async function resolve(workspaceId: string | undefined): Promise<WorkspaceContext> {
  const me = await fetchMe();

  if (me === null) {
    // Capture where the user was heading so sign-in can return them here on a
    // cold click (IMM-486), rather than discarding the deep link to `/sign-in`.
    throw redirect(signInRedirectForCurrentLocation());
  }

  // The workspace comes from the URL (`/w/:workspaceId`, a slug or a UUID). An
  // absent value (e.g. an internal default call) or one that resolves to no
  // membership (stale/forged link) bounces to the default workspace so the
  // address bar self-corrects rather than 404.
  const group = workspaceId !== undefined ? resolveWorkspaceGroup(me, workspaceId) : null;
  const membership = group !== null ? membershipForWorkspace(me, group.id) : null;

  if (group === null || membership === null) {
    const fallbackSlug = resolveDefaultWorkspaceSlug(me);

    if (fallbackSlug === null) {
      throw redirect('/support/not-registered');
    }

    throw redirect(workspacePath(fallbackSlug));
  }

  const { workspaceId: resolvedWorkspaceId } = membership;
  const bootstrapPermissions = await fetchMemberPermissions(membership.id);
  const orgs = await listWorkspaceOrganizations(resolvedWorkspaceId, bootstrapPermissions);

  if (!orgs.ok) {
    throw new Response('Failed to load workspace organisations', { status: orgs.status });
  }

  if (orgs.body.data.length === 0) {
    throw new Response('Workspace has no organisation', { status: 404 });
  }

  // Pre-select the first Standort the member can actually act on when none is
  // saved (or the saved one is no longer accessible) — context-only ancestor
  // rows (`accessible === false`) merely root the tree and are not selectable.
  const accessibleOrgs = orgs.body.data.filter((o) => o.accessible !== false);
  const saved = resolveSelectedOrganization(orgs.body.data);
  const organization =
    saved !== undefined && saved.accessible !== false ? saved : (accessibleOrgs[0] ?? orgs.body.data[0]!);

  const permissions =
    organization.id === membership.id ? bootstrapPermissions : await fetchMemberPermissions(organization.id);

  const savedNarrow = readNarrowedOrganizationId();
  const narrowedOrganizationId = orgs.body.data.some((o) => o.id === savedNarrow) ? savedNarrow : null;

  return {
    me,
    workspaceId: resolvedWorkspaceId,
    workspaceSlug: group.slug,
    organization,
    organizations: orgs.body.data,
    permissions,
    narrowedOrganizationId,
    seesAllStandorte: canReadAllWorkspaceOrganizations(permissions),
  };
}

export function loadWorkspaceContext(workspaceId?: string): Promise<WorkspaceContext> {
  const key = workspaceId ?? '__default__';

  if (cache === null || cache.key !== key) {
    cache = {
      key,
      promise: resolve(workspaceId).catch((error: unknown) => {
        // Don't cache failures — a thrown redirect/Response must re-resolve on the
        // next navigation rather than replay a stale rejection forever.
        if (cache?.key === key) cache = null;
        throw error;
      }),
    };
  }

  return cache.promise;
}

export function invalidateWorkspaceContext(): void {
  cache = null;
}
